import React, { useContext, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { useSearchParams, useParams } from "../../hooks/useNavigation";
import PropTypes from "prop-types";
import styled, { ThemeContext } from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import useIsHydrated from "../../hooks/useIsHydrated";
import CustomSelect from "../../CustomSelect/CustomSelect";
import useDevice from "../../customHooks/useDevice";
import Loader from "../Loader/Loader";
import FrontendSelectFilter from "./GridFilters/FrontendSelectFilter";
import GridVacancyList from "./GridVacancyList";
import NoVacancies from "./NoVacancies";
import StyledPagination from "./StyledPagination";
import StyledArrowPagination from "./ThumbnailSearchResults/ThumbanailSearchResultsPagination";

const ITEMS_PER_PAGE = 12;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "column" : "row")};
  gap: 1rem;
  padding: 1rem 0;
`;

const StyledLink = styled.div`
  font-weight: 600;
  font-size: 0.8rem;
  text-decoration: underline;
  cursor: pointer;
  min-width: 100px;
  margin: auto;
`;

function GridWithHeaderFilterSearchResults(props) {
  const { isLoading } = props;

  const {
    displayVancanciesCount = true,
    additionalConfig = {},
    itemsPerPage,
  } = useContext(ComponentContext);
  const { isSmallScreen, isMobile } = useDevice();
  const { filtersStore } = useStores();
  const {
    searchResults,
    resetAllFilters,
    frontendFilters,
    pagination,
    isFetching,
    setFrontendFiltersFromQuery,
    jobCardType = "gridWithHeaderFilter",
  } = filtersStore;

  const itemsToDisplay = itemsPerPage || ITEMS_PER_PAGE;
  const theme = useContext(ThemeContext);
  const [paginatedSearchResults, setPaginatedSearchResults] =
    useState(searchResults);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [resultsCount, setResultsCount] = useState(0);
  const [resultsOrder, setResultsOrder] = useState("DESC");

  const params = useParams();
  const searchParams = useSearchParams();
  const searchObj = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...searchObj };

  const isHydrated = useIsHydrated();
  const isServer = !isHydrated;

  const filteredSearchResults = searchResults.filter((result) => {
    if (
      isEmpty(frontendFilters.hours) &&
      isEmpty(frontendFilters.country) &&
      isEmpty(frontendFilters.role)
    ) {
      return true;
    }

    let passesCategoryFilter = isEmpty(frontendFilters.role);
    let passesHoursFilter = isEmpty(frontendFilters.hours);
    let passesCountryFilter = isEmpty(frontendFilters.country);

    if (!passesCategoryFilter) {
      passesCategoryFilter = frontendFilters.role.includes(result.category);
    }

    if (!passesHoursFilter) {
      passesHoursFilter = frontendFilters.hours.includes(result.hoursType);
    }

    if (!passesCountryFilter) {
      passesCountryFilter = frontendFilters.country.includes(
        result.location.country
      );
    }

    return passesCategoryFilter && passesHoursFilter && passesCountryFilter;
  });

  useEffect(() => {
    const endOffset = currentPage + itemsToDisplay;

    setPaginatedSearchResults(
      filteredSearchResults.slice(currentPage, endOffset)
    );
    setPageCount(Math.ceil(filteredSearchResults.length / itemsToDisplay));
    setResultsCount(filteredSearchResults.length);
  }, [currentPage, itemsToDisplay, JSON.stringify(filteredSearchResults)]);

  // On sort change — refetch from API
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) return;
    filtersStore.search({ order: resultsOrder });
    setCurrentPage(0);
  }, [resultsOrder]);

  const searchParamsString = searchParams.toString();

  // On mount and URL query change — always fetch fresh data (1 call)
  useEffect(() => {
    setFrontendFiltersFromQuery(combindedParms);
    filtersStore.search({ order: resultsOrder }).then(() => {
      hasMounted.current = true;
    });
    setCurrentPage(0);
  }, [searchParamsString]);

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsToDisplay) % pagination.totalResults;
    setCurrentPage(newOffset);
  };

  const listClassName = classNames("col container-xxl");

  const roleSelectStyles = {
    container: (provided) => {
      return {
        ...provided,
        height: "100%",
        width: "100%",
      };
    },
    control: (provided) => ({
      ...provided,
      background: "white",
      borderRadius: "10px",
      boxShadow: "none",
      fontWeight: "400",
      height: "100%",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
    }),
    option: (provided) => ({
      ...provided,
      background: theme.primaryBackgroundColor,
      color: "black",
      height: "100%",
    }),
    placeholder: (provided) => ({
      ...provided,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "black",
    }),
  };

  const paginationComponent = additionalConfig?.showArrowPagination ? (
    <StyledArrowPagination
      currentPage={currentPage}
      handlePageClick={handlePageClick}
      itemsPerPage={itemsToDisplay}
      pageCount={pageCount}
      searchResultsCount={pagination.totalResults}
    />
  ) : (
    <StyledPagination handlePageClick={handlePageClick} pageCount={pageCount} />
  );

  const content = (
    <>
      <div className="mb-5 mt-5">
        <GridVacancyList
          displayVancanciesCount={displayVancanciesCount}
          isLoading={isFetching}
          resultsCount={resultsCount}
          searchResults={paginatedSearchResults}
          type={additionalConfig?.cardType || jobCardType}
        />
      </div>
      {pagination.totalResults > itemsToDisplay ? paginationComponent : null}
    </>
  );

  const isEmptyResults = isEmpty(paginatedSearchResults);

  const sortButton = (
    <CustomSelect
      defaultValue={{ label: "Latest", value: "DESC" }}
      id="role"
      onChange={(ord) => {
        setResultsOrder(ord.value);
      }}
      options={[
        { label: "Oldest", value: "ASC" },
        { label: "Latest", value: "DESC" },
      ]}
      styles={roleSelectStyles}
    />
  );

  return (
    <div>
      <div className="container-xxl row g-5 m-auto">
        <StyledContainer isMobile={isMobile || isSmallScreen}>
          <FrontendSelectFilter
            isLoading={isFetching || isServer}
            label={additionalConfig?.searchResultsGridRoleLabelText || "Role"}
            type="role"
          />
          <FrontendSelectFilter
            isLoading={isFetching || isServer}
            label={additionalConfig?.searchResultsGridHoursLabelText || "Hours" }
            type="hours"
          />
          {isFetching || isServer ? null : sortButton}
          {isFetching || isServer ? null : (
            <StyledLink onClick={() => resetAllFilters()}>Clear all</StyledLink>
          )}
        </StyledContainer>
        <div className={listClassName}>
          {isLoading && <Loader minHeight="450px" />}
          {isEmptyResults && <NoVacancies />}
          {!isLoading && !isFetching && !isEmptyResults && content}
        </div>
      </div>
    </div>
  );
}

export default observer(GridWithHeaderFilterSearchResults);

GridWithHeaderFilterSearchResults.propTypes = {
  isLoading: PropTypes.bool,
};

GridWithHeaderFilterSearchResults.defaultProps = {
  isLoading: false,
};
